import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Tag, Typography, Space, Modal, Table, Badge
} from 'antd';
import {
  WifiOutlined, DisconnectOutlined, ApiOutlined, EnvironmentOutlined,
  CloudOutlined, DatabaseOutlined, EyeOutlined
} from '@ant-design/icons';
import { getReadersSummary, getReaders } from '../api';

const { Title, Text } = Typography;

const STATUS_COLOR = { online: '#52c41a', offline: '#ff4d4f', idle: '#faad14' };
const CONN_ICON    = { LAN: <DatabaseOutlined />, WiFi: <WifiOutlined />, '4G': <CloudOutlined /> };

export default function ReaderDashboard() {
  const [summary, setSummary]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [detailReaders, setDetailReaders] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getReadersSummary().then(r => setSummary(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openDetail = (evt) => {
    setDetailModal(evt);
    setDetailLoading(true);
    getReaders({ event_id: evt.id }).then(r => setDetailReaders(r.data.data || [])).catch(() => {}).finally(() => setDetailLoading(false));
  };

  const detailColumns = [
    { title: 'Reader Name', dataIndex: 'reader_name', render: v => <Text strong>{v}</Text> },
    { title: 'Brand', dataIndex: 'reader_brand', render: v => v || '-' },
    { title: 'Technology', dataIndex: 'technology', render: v => <Tag color="purple">{v}</Tag> },
    { title: 'Ports', dataIndex: 'ports', width: 60, align: 'center' },
    { title: 'Mode', dataIndex: 'reader_mode', render: v => <Tag color="geekblue">{v}</Tag> },
    {
      title: 'Connectivity', dataIndex: 'connectivity',
      render: v => <Tag icon={CONN_ICON[v] || <WifiOutlined />}>{v}</Tag>,
    },
    { title: 'Push To', dataIndex: 'push_to', render: v => <Tag color={v === 'Cloud' ? 'blue' : v === 'Both' ? 'purple' : 'default'}>{v}</Tag> },
    { title: 'IP', dataIndex: 'ip_address', render: v => <Text code style={{ fontSize: 10 }}>{v || '-'}</Text> },
    { title: 'Location', dataIndex: 'location_name', render: v => v || '-' },
    {
      title: 'Status', dataIndex: 'status',
      render: v => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
    },
  ];

  return (
    <div>
      <Row align="middle" style={{ marginBottom: 20 }}>
        <Col flex="1">
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <ApiOutlined style={{ marginRight: 8 }} />Reader Dashboard
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Event-wise reader overview — click a card for full details</Text>
        </Col>
      </Row>

      {/* Event cards */}
      <Row gutter={[16, 16]}>
        {summary.map(evt => (
          <Col xs={24} sm={12} md={8} lg={6} key={evt.id}>
            <Card
              hoverable
              style={{ borderRadius: 12, borderTop: '4px solid #0F1D6B', cursor: 'pointer' }}
              onClick={() => openDetail(evt)}
              actions={[
                <span key="detail" style={{ fontSize: 12, color: '#0F1D6B' }}><EyeOutlined /> View Details</span>
              ]}>
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 15, display: 'block' }}>{evt.event_name}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{evt.event_code}</Text>
              </div>

              <div style={{ fontSize: 36, fontWeight: 800, color: '#0F1D6B', textAlign: 'center', lineHeight: 1, marginBottom: 8 }}>
                {evt.total_readers || 0}
              </div>
              <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 11, marginBottom: 12 }}>Total Readers</Text>

              <Row gutter={8}>
                <Col span={8} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#52c41a' }}>{evt.online || 0}</div>
                  <Text style={{ fontSize: 10, color: '#52c41a' }}>Online</Text>
                </Col>
                <Col span={8} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#faad14' }}>{evt.idle || 0}</div>
                  <Text style={{ fontSize: 10, color: '#faad14' }}>Idle</Text>
                </Col>
                <Col span={8} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#ff4d4f' }}>{evt.offline || 0}</div>
                  <Text style={{ fontSize: 10, color: '#ff4d4f' }}>Offline</Text>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}
        {!loading && !summary.length && (
          <Col span={24}>
            <Card style={{ textAlign: 'center', padding: 40, borderRadius: 12 }}>
              <ApiOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <div style={{ color: '#999', marginTop: 12 }}>No events found. Create an event and configure readers to see the dashboard.</div>
            </Card>
          </Col>
        )}
      </Row>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <ApiOutlined />
            <span>Readers for: <strong>{detailModal?.event_name}</strong></span>
            <Tag>{detailModal?.event_code}</Tag>
          </Space>
        }
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={null}
        width={1000}>

        {/* Summary stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {[
            { label: 'Total Readers', value: detailReaders.length, color: '#0F1D6B' },
            { label: 'Online', value: detailReaders.filter(r => r.status === 'online').length, color: '#52c41a' },
            { label: 'UHF', value: detailReaders.filter(r => r.technology === 'UHF').length, color: '#722ed1' },
            { label: 'LAN', value: detailReaders.filter(r => r.connectivity === 'LAN').length, color: '#1890ff' },
          ].map((s, i) => (
            <Col span={6} key={i}>
              <Card size="small" style={{ textAlign: 'center', borderTop: `3px solid ${s.color}`, borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{s.label}</div>
              </Card>
            </Col>
          ))}
        </Row>

        <Table columns={detailColumns} dataSource={detailReaders} rowKey="id"
          loading={detailLoading} size="small" scroll={{ x: 900 }}
          pagination={{ pageSize: 10 }} />
      </Modal>
    </div>
  );
}
